import React, { forwardRef, useImperativeHandle } from "react";
import classNames from "classnames";
import { MdOutlinePhoto } from "react-icons/md";
// import { ShoppingCartItem } from "components/ShoppingCart";
import { ShopItemViewRef, ShopItemViewProps } from "./ShopItemView.types";
import { useShopItemView } from "./ShopItemView.hooks";
import { SHOP_CONFIG } from "../ShopList.config";
import {
  createCartItemFromShopData,
  getDisplayName,
  getDisplayCode,
  getDisplayDescription,
  getDisplayDeliveryTime,
  getDisplayPrice,
  getImageProps,
} from "./ShopItemView.utils";
import "./ShopItemView.scss";

const ShopItemView = forwardRef<ShopItemViewRef, ShopItemViewProps>(
  ({ onClose, onAddToCart }, ref) => {
    const {
      isOpen,
      selectedItem,
      selectedItemId,
      quantity,
      shopItemData,
      loading,
      error,
      open,
      close,
      toggle,
      incrementQuantity,
      decrementQuantity,
    } = useShopItemView();

    useImperativeHandle(ref, () => ({
      open,
      close,
      toggle,
    }));

    const handleClose = () => {
      close();
      onClose?.();
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    };

    const handleAddToCart = () => {
      if (selectedItem && onAddToCart) {
        const cartItem = createCartItemFromShopData(selectedItem, shopItemData?.details, quantity);
        onAddToCart(cartItem);
        handleClose();
      }
    };

    // Don't render anything if we've never had a selected item
    if (!selectedItem && !isOpen) return null;

    const imageProps = getImageProps(shopItemData?.details, selectedItem!);

    return (
      <>
        <div
          className={classNames("ShopItemViewOverlay", { open: isOpen })}
          data-testid="shop-view-overlay"
          onClick={handleOverlayClick}
        />
        <div
          className={classNames("ShopItemViewPanel", { open: isOpen })}
          data-testid="shop-view-panel"
        >
          {selectedItem && (
            <>
              <div className="ItemViewHeader">
                <h2 className="ItemViewTitle" data-testid="shop-view-title">
                  Item Detail
                </h2>
                <button
                  className="CloseButton"
                  data-testid="shop-view-close"
                  onClick={handleClose}
                  aria-label="Close item view"
                >
                  ×
                </button>
              </div>

              <div className="ItemViewContent">
                {loading && selectedItemId && (
                  <div className="LoadingState">
                    <p>Loading item details...</p>
                  </div>
                )}

                {error && selectedItemId && (
                  <div className="ErrorState">
                    <p>Failed to load item details. Showing basic information.</p>
                  </div>
                )}

                {/* Only show item details when not loading or when there's no API call needed */}
                {(!loading || !selectedItemId) && (
                  <>
                    <h3 className="ItemName" data-testid="shop-view-name">
                      {getDisplayName(shopItemData?.details, selectedItem)}
                    </h3>

                    <div className="ItemImage" data-testid="shop-view-image">
                      {imageProps.src ? (
                        <img
                          src={imageProps.src}
                          alt={imageProps.alt}
                          className="large-item-image"
                        />
                      ) : (
                        <div
                          className="large-photo-placeholder"
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#cccccc",
                          }}
                        >
                          {MdOutlinePhoto({ size: "60%" })}
                        </div>
                      )}
                    </div>

                    <div className="ItemDetails">
                      <div className="ItemCodeSection">
                        <span className="label">Item code:</span>
                        <span className="value" data-testid="shop-view-code">
                          {getDisplayCode(shopItemData?.details, selectedItem)}
                        </span>
                      </div>

                      <div className="DetailedDescription">
                        <span className="label">Description</span>
                        <p className="description-text" data-testid="shop-view-desc">
                          {getDisplayDescription(shopItemData?.details, selectedItem)}
                        </p>
                      </div>

                      <div className="DeliveryTime">
                        <span className="label">Estimated delivery time</span>
                        <p className="delivery-text">
                          {getDisplayDeliveryTime(shopItemData?.details)}
                        </p>
                      </div>

                      <div className="PriceSection">
                        <span className="label">Price</span>
                        <p className="price-text" data-testid="shop-view-price">
                          {shopItemData?.details?.currencySymbol ||
                            SHOP_CONFIG.FALLBACK_CURRENCY_SYMBOL}
                          {getDisplayPrice(shopItemData?.details, selectedItem).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="ItemViewFooter">
                <div className="QuantityControls">
                  <button
                    className="QuantityButton"
                    data-testid="shop-view-qty-dec"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span className="QuantityValue" data-testid="shop-view-qty">
                    {quantity}
                  </span>
                  <button
                    className="QuantityButton"
                    data-testid="shop-view-qty-inc"
                    onClick={incrementQuantity}
                  >
                    +
                  </button>
                </div>
                <button
                  className="AddToCartButton"
                  data-testid="shop-view-add-cart"
                  onClick={handleAddToCart}
                >
                  Add to basket
                </button>
              </div>
            </>
          )}
        </div>
      </>
    );
  }
);

ShopItemView.displayName = "ShopItemView";

export default ShopItemView;
export type { ShopItemViewRef, ShopItemViewProps } from "./ShopItemView.types";
export { useCloseShopItemView } from "./ShopItemView.hooks";
